// Klub → Aktivita členek (25.10.2026, zadání Ládi, mockup schválený 25.10.).
//
// PROČ: sloupec „Naposledy“ u Členů ukazoval jen přihlášení kódem z e-mailu,
// které vydrží 90 dní. Nešlo z něj poznat, kdo klub opravdu používá a kdo se
// ve zkušebním týdnu ztrácí, dokud se jí dá ještě ozvat. Klub teď ukládá,
// kdy členka aplikaci otevřela a co v ní dělala (jen počty za den, žádný
// obsah), viz klub: netlify/functions/_lib/klubAktivita.mjs.
//
// Vidí owner a CSM (Tinka řeší péči o členky). Data se drží 12 měsíců.
import React, { useMemo, useRef, useState } from "react";
import { Activity, RefreshCw, Mail, X } from "lucide-react";
import { useApiData } from "../../lib/api.js";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, RozbalovaciVyber, ErrorNote, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, AkceHlavicky } from "../../components/PrehledDlazdice.jsx";

const MERIME_OD = "25. 10. 2026";
const MERIME_OD_ISO = "2026-10-25";
const ZKUSEBNI_DNI = 8; // FAPI: zkušební doba 8 dní, platba přijde 8. den

// ── datumy (dny jako "YYYY-MM-DD" v pražském čase) ──────────────────────
const isoPraha = (d) => new Date(d).toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
const plusDni = (iso, n) => {
  const [r, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(r, m - 1, d + n)).toISOString().slice(0, 10);
};
const rozdilDni = (a, b) => Math.round((Date.UTC(...a.split("-").map((x, i) => (i === 1 ? x - 1 : +x))) - Date.UTC(...b.split("-").map((x, i) => (i === 1 ? x - 1 : +x)))) / 86400000);
// Nezlomitelná mezera, ať se „28. 10.“ nerozdělí na dva řádky.
const kratce = (iso) => { const [, m, d] = iso.split("-").map(Number); return `${d}.\u00a0${m}.`; };
const DNY_TYDNE = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
const denTydne = (iso) => DNY_TYDNE[new Date(`${iso}T12:00:00Z`).getUTCDay()];
const cas = (ts) => new Date(ts).toLocaleTimeString("cs-CZ", { timeZone: "Europe/Prague", hour: "numeric", minute: "2-digit" });

// ── události → srozumitelné kategorie ───────────────────────────────────
// Hlavní čtyři se ukazují vždycky (i s nulou), ostatní jen když něco mají.
const KATEGORIE = [
  { id: "jidelnicek", nazev: "Jídelníček", hlavni: true, test: (u) => u.startsWith("sekce:jidelnicek") },
  { id: "navyky", nazev: "Návyky", hlavni: true, test: (u) => u === "navyk" },
  { id: "trenink", nazev: "Trénink", hlavni: true, test: (u) => u === "video:trenink" || u === "video:movement" },
  { id: "denik", nazev: "Deník", hlavni: true, test: (u) => u === "denik-prispevek" || u === "denik-reakce" },
  { id: "navod", nazev: "Video návod", test: (u) => u === "video:navod" },
  { id: "podcast", nazev: "Podcast", test: (u) => u === "video:podcast" },
  { id: "vysilani", nazev: "Vysílání", test: (u) => u.startsWith("sekce:vysilani") },
  { id: "profil", nazev: "Profil", test: (u) => u.startsWith("sekce:profil") },
];
const NAZEV_SEKCE = {
  prehled: "Přehled", jidelnicek: "Jídelníček", denik: "Deník", obsah: "Obsah", vice: "Více",
  zvyky: "Návyky", profil: "Profil", vysilani: "Vysílání", clenstvi: "Členství",
};
const NAZEV_PODSEKCE = {
  navod: "Video návod", podcast: "Podcast", treninky: "Tréninky", "movement-restart": "Movement Restart",
  videoteka: "Videotéka", zaznamy: "Záznamy",
};
const NAZEV_VIDEA = { trenink: "Pustila trénink", movement: "Pustila Movement Restart", navod: "Pustila video návod", podcast: "Pustila podcast", videoteka: "Pustila video z videotéky", zaznam: "Pustila záznam vysílání" };

function popisUdalosti(u, pocet) {
  const n = pocet > 1 ? ` ${pocet}×` : "";
  if (u === "otevreni") return `otevřela${n || " 1×"}`;
  if (u === "navyk") return `Návyky${n}`;
  if (u === "denik-prispevek") return `Deník: příspěvek${n}`;
  if (u === "denik-reakce") return `Deník: reakce${n}`;
  if (u.startsWith("video:")) {
    const d = u.slice(6).split("/")[0];
    return NAZEV_VIDEA[d] || `Pustila video (${NAZEV_SEKCE[d] || d})`;
  }
  if (u.startsWith("sekce:")) {
    const [s, p] = u.slice(6).split("/");
    return p ? (NAZEV_PODSEKCE[p] || `${NAZEV_SEKCE[s] || s}: ${p}`) : (NAZEV_SEKCE[s] || s);
  }
  return u;
}

// Jak moc byla den aktivní: 0 nebyla, 1 jen otevřela, 2 něco dělala, 3 hodně.
const jenOtevrela = (u) => u === "otevreni" || u === "sekce:prehled";
function silaDne(udalosti) {
  if (!udalosti.length) return 0;
  const akce = udalosti.filter((x) => !jenOtevrela(x.udalost)).reduce((s, x) => s + (x.pocet || 0), 0);
  if (akce === 0) return 1;
  return akce >= 5 ? 3 : 2;
}
const BARVA_DNE = ["bg-slate-100", "bg-brand-200", "bg-brand-400", "bg-brand"];

// ── výpočet jedné členky ────────────────────────────────────────────────
function rozeber(c, dnes, dni) {
  const poDnech = new Map();
  for (const x of c.udalosti || []) {
    if (!poDnech.has(x.den)) poDnech.set(x.den, []);
    poDnech.get(x.den).push(x);
  }
  const posledniDen = c.posledni ? isoPraha(c.posledni) : null;
  const pred = posledniDen ? rozdilDni(dnes, posledniDen) : null;
  let stav;
  if (pred === null) stav = { id: "bez", text: "Zatím bez záznamu", ton: "seda" };
  else if (pred <= 2) stav = { id: "aktivni", text: "Aktivní", ton: "zelena" };
  else if (pred <= 6) stav = { id: "ztraci", text: "Ztrácí se", ton: "cervena" };
  else stav = { id: "nechodi", text: "Nechodí", ton: "cervena" };

  let naposledy = "zatím bez záznamu";
  if (pred === 0) naposledy = `dnes ${cas(c.posledni)}`;
  else if (pred === 1) naposledy = `včera ${cas(c.posledni)}`;
  else if (pred !== null) naposledy = `před ${pred} dny, ${kratce(posledniDen)}`;

  const pruh = [];
  for (let i = dni - 1; i >= 0; i -= 1) {
    const den = plusDni(dnes, -i);
    pruh.push({ den, sila: silaDne(poDnech.get(den) || []) });
  }

  const od7 = plusDni(dnes, -6);
  const pocitej = (odDne) => KATEGORIE.map((k) => ({
    ...k,
    pocet: (c.udalosti || []).filter((x) => x.den >= odDne && k.test(x.udalost)).reduce((s, x) => s + (x.pocet || 0), 0),
  }));

  const platba = c.zFapi && c.zacatek ? plusDni(c.zacatek, ZKUSEBNI_DNI) : null;
  const zkusebni = platba && dnes < platba && !c.zrusila ? platba : null;

  // Pořadí: kdo se ztrácí (nejdéle pryč nahoře), pak bez záznamu, pak aktivní.
  const poradi = stav.id === "ztraci" || stav.id === "nechodi" ? 1000 + pred : stav.id === "bez" ? 500 : pred;

  return { ...c, poDnech, pred, stav, naposledy, pruh, tyden: pocitej(od7), obdobi: pocitej(plusDni(dnes, -(dni - 1))), zkusebni, poradi };
}

// ── drobné komponenty ───────────────────────────────────────────────────
const TON_PILULKY = {
  seda: "border-slate-200 bg-slate-100 text-slate-600",
  zelena: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cervena: "border-rose-200 bg-rose-50 text-rose-700",
  zluta: "border-amber-200 bg-amber-50 text-amber-800",
  fialova: "border-brand-200 bg-brand-50 text-brand-700",
};
function Stitek({ ton = "seda", children }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TON_PILULKY[ton]}`}>
      {children}
    </span>
  );
}

function PilulkyClenky({ c }) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {c.zkusebni && <Stitek ton="zluta">Zkušební do {kratce(c.zkusebni)}</Stitek>}
      {c.varianta === "q" && <Stitek>3 měsíce</Stitek>}
      {c.varianta === "m" && <Stitek>Měsíčně</Stitek>}
      {!c.zFapi && <Stitek>ručně</Stitek>}
      {c.zrusila && <Stitek ton="cervena">Zrušila členství</Stitek>}
    </div>
  );
}

function Pruh({ pruh }) {
  return (
    <div>
      <div className="flex gap-[3px]">
        {pruh.map((d) => (
          <span key={d.den} title={`${kratce(d.den)} ${["nebyla", "jen otevřela", "něco dělala", "hodně aktivní"][d.sila]}`}
            className={`block h-[22px] min-w-[6px] flex-1 max-w-[16px] rounded ${BARVA_DNE[d.sila]}`} />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-400" style={{ maxWidth: `${pruh.length * 19}px` }}>
        <span>{kratce(pruh[0].den)}</span><span>dnes</span>
      </div>
    </div>
  );
}

function CoDela({ kategorie }) {
  const videt = kategorie.filter((k) => k.hlavni || k.pocet > 0);
  return (
    <div className="flex flex-wrap gap-1.5">
      {videt.map((k) => (
        <Stitek key={k.id} ton={k.pocet > 0 ? "fialova" : "seda"}>{k.nazev} {k.pocet}×</Stitek>
      ))}
    </div>
  );
}

function Legenda() {
  const pol = [["nebyla", 0], ["jen otevřela", 1], ["něco dělala", 2], ["hodně aktivní", 3]];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 px-4 py-3 text-xs text-slate-500 sm:px-5">
      <span>Den v aplikaci:</span>
      {pol.map(([t, s]) => (
        <span key={t} className="inline-flex items-center gap-1.5"><span className={`inline-block h-3 w-3 rounded ${BARVA_DNE[s]}`} />{t}</span>
      ))}
    </div>
  );
}

function Cislo({ popisek, hodnota, doplnek, varovani = false }) {
  return (
    <div className="glass rounded-xl px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{popisek}</div>
      <div className={`mt-1.5 text-2xl font-bold tabular-nums sm:text-3xl ${varovani ? "text-rose-700" : "text-slate-900"}`}>{hodnota}</div>
      <div className="mt-0.5 text-xs text-slate-500">{doplnek}</div>
    </div>
  );
}

// ── detail členky ───────────────────────────────────────────────────────
function Detail({ c, dnes, dni, onZavrit }) {
  // Den po dni od nejnovějšího. Dny bez aktivity se slučují do rozsahu,
  // ať se nemusí projíždět dlouhý seznam prázdných dnů. Začíná se dnem,
  // kdy přišla do klubu, nebo začátkem měření (dřív záznamy nejsou, prázdné
  // dny by tam jen strašily). Nejstarší záznam ale nikdy neusekne.
  const odObdobi = plusDni(dnes, -(dni - 1));
  const nejstarsi = [...c.poDnech.keys()].sort()[0] || null;
  let start = [odObdobi, c.zacatek || "0000-00-00", MERIME_OD_ISO].sort().pop();
  if (nejstarsi && nejstarsi < start) start = nejstarsi < odObdobi ? odObdobi : nejstarsi;
  const radky = [];
  let prazdne = [];
  const uzavri = () => {
    if (!prazdne.length) return;
    const a = prazdne[prazdne.length - 1]; const b = prazdne[0];
    radky.push({ prazdne: true, text: a === b ? kratce(a) : `${kratce(a)} až ${kratce(b)}`, klic: `p${a}` });
    prazdne = [];
  };
  for (let den = dnes; den >= start; den = plusDni(den, -1)) {
    const u = c.poDnech.get(den);
    if (!u) { prazdne.push(den); continue; }
    uzavri();
    const serazene = [...u].sort((x, y) => (x.udalost === "otevreni" ? -1 : y.udalost === "otevreni" ? 1 : (y.pocet || 0) - (x.pocet || 0)));
    radky.push({ den, klic: den, pilulky: [...new Set(serazene.map((x) => popisUdalosti(x.udalost, x.pocet)))] });
  }
  uzavri();
  const max = Math.max(1, ...c.obdobi.map((k) => k.pocet));

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Activity}
        nazev={c.jmeno || c.email}
        podtitulek={`Detail · členka od ${c.zacatek ? kratce(c.zacatek) : "?"}${c.zkusebni ? `, zkušební týden do ${kratce(c.zkusebni)}` : ""}`}
        akce={<AkceHlavicky ikona={X} popis="Zavřít detail" onClick={onZavrit} />}
      />
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="px-4 py-4 sm:px-5">
          <h3 className="mb-3 font-serif text-lg font-semibold text-slate-900">Den po dni</h3>
          {radky.length === 0 ? (
            <p className="text-sm text-slate-500">Zatím bez záznamu. Aktivitu měříme od {MERIME_OD}.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {radky.map((r) => (
                <div key={r.klic} className="flex items-start gap-3">
                  <div className="w-[78px] shrink-0 pt-0.5 text-[13px] font-semibold text-slate-800">
                    {r.prazdne ? r.text : kratce(r.den)}
                    {!r.prazdne && <span className="block font-normal text-slate-400">{denTydne(r.den)}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.prazdne
                      ? <Stitek ton="cervena">nebyla v aplikaci</Stitek>
                      : r.pilulky.map((p, i) => <Stitek key={p} ton={i === 0 && p.startsWith("otevřela") ? "seda" : "fialova"}>{p}</Stitek>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 px-4 py-4 sm:px-5 md:border-l md:border-t-0">
          <h3 className="mb-3 font-serif text-lg font-semibold text-slate-900">Co používá ({dni} dní)</h3>
          <div className="flex flex-col gap-2.5">
            {c.obdobi.filter((k) => k.hlavni || k.pocet > 0).map((k) => (
              <div key={k.id} className="grid grid-cols-[110px_1fr_36px] items-center gap-2.5 text-[13px] text-slate-700">
                <span>{k.nazev}</span>
                <span className="block h-2.5 overflow-hidden rounded-full bg-brand-50">
                  <span className="block h-full rounded-full bg-brand" style={{ width: `${Math.round((k.pocet / max) * 100)}%` }} />
                </span>
                <span className="text-right font-semibold tabular-nums">{k.pocet}×</span>
              </div>
            ))}
          </div>
          <a href={`mailto:${c.email}`} className="mt-4 inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-brand-700 sm:hover:bg-slate-50">
            <Mail className="h-4 w-4" /> Napsat e-mail
          </a>
        </div>
      </div>
    </KartaPrehledu>
  );
}

// ── stránka ─────────────────────────────────────────────────────────────
export default function KlubAktivita() {
  const [obdobi, setObdobi] = useState("14");
  const [filtr, setFiltr] = useState("vse");
  const [vybrana, setVybrana] = useState(null);
  const detailRef = useRef(null);
  const uzka = useUzkaObrazovka();
  const { data, loading, error, refetch } = useApiData(`/api/klub-aktivita?dni=${obdobi}`);

  const dni = Number(obdobi);
  const { clenky, cisla } = useMemo(() => {
    if (!data || !data.dnes) return { clenky: [], cisla: null };
    const dnes = data.dnes;
    const vse = (data.clenky || []).map((c) => rozeber(c, dnes, dni)).sort((a, b) => b.poradi - a.poradi);
    const zkusebni = vse.filter((c) => c.zkusebni);
    return {
      clenky: vse,
      cisla: {
        dnes: vse.filter((c) => c.pred === 0).length,
        tyden: vse.filter((c) => c.pred !== null && c.pred <= 6).length,
        ztraci: vse.filter((c) => c.stav.id === "ztraci" || c.stav.id === "nechodi").length,
        zkusebni: zkusebni.length,
        platba: zkusebni.map((c) => c.zkusebni).sort()[0] || null,
        celkem: vse.length,
      },
    };
  }, [data, dni]);

  const videt = clenky.filter((c) =>
    filtr === "ztraci" ? (c.stav.id === "ztraci" || c.stav.id === "nechodi")
      : filtr === "zkusebni" ? Boolean(c.zkusebni) : true);
  const detail = vybrana ? clenky.find((c) => c.email === vybrana) : null;
  const otevriDetail = (email) => {
    setVybrana(email);
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const filtry = [
    { key: "vse", label: `Všechny aktivní (${clenky.length})` },
    { key: "ztraci", label: `Ztrácí se (${cisla?.ztraci || 0})` },
    { key: "zkusebni", label: `Zkušební týden (${cisla?.zkusebni || 0})` },
  ];
  const tlacitko = "inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-brand-700 sm:hover:bg-slate-50";

  return (
    <Stranka>
      <GradientHeader
        title="Aktivita členek"
        icon={Activity}
        loading={loading}
        napoveda={`Kdy členky klub otevírají a co v něm dělají. Ukládá se jen počet za den, žádný obsah. Aktivitu měříme od ${MERIME_OD}, data držíme 12 měsíců. „Ztrácí se“ = 3 až 6 dní nebyla v aplikaci, „Nechodí“ = 7 a víc dní.`}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading }]}
      />

      {error && <ErrorNote onRetry={refetch}>Aktivitu se nepodařilo načíst: {String(error)}</ErrorNote>}

      {cisla && (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <Cislo popisek="Dnes v aplikaci" hodnota={cisla.dnes} doplnek={`ze ${cisla.celkem} aktivních členek`} />
          <Cislo popisek="Aktivní za 7 dní" hodnota={`${cisla.tyden} / ${cisla.celkem}`} doplnek="aspoň jednou otevřela" />
          <Cislo popisek="Ztrácí se" hodnota={cisla.ztraci} doplnek="3 a víc dní nebyla" varovani={cisla.ztraci > 0} />
          <Cislo popisek="Zkušební týden" hodnota={cisla.zkusebni} doplnek={cisla.platba ? `nejbližší platba ${kratce(cisla.platba)}` : "nikdo"} />
        </div>
      )}

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Activity}
          nazev="Členky"
          podtitulek={`seřazené od těch, které se ztrácí · měříme od ${MERIME_OD}`}
          odznak={`${videt.length} zobrazených`}
        />
        <div className="grid grid-cols-1 gap-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:grid-cols-2 sm:p-4">
          <RozbalovaciVyber stitek="Zobrazit" aria-label="Které členky zobrazit" value={filtr} onChange={setFiltr} items={filtry} />
          <RozbalovaciVyber stitek="Období" aria-label="Období" value={obdobi} onChange={setObdobi}
            items={[{ key: "14", label: "Posledních 14 dní" }, { key: "30", label: "Posledních 30 dní" }]} />
        </div>

        {loading && !data && <div className="p-3 sm:p-4"><SkeletonRows rows={5} /></div>}

        {data && (videt.length === 0 ? (
          <EmptyState icon={Activity} title="Žádná členka neodpovídá filtru" hint="Zkus jiný výběr." />
        ) : uzka ? (
          <div className="divide-y divide-slate-100">
            {videt.map((c) => (
              <div key={c.email} className="px-4 py-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-800">
                      {c.jmeno || c.email}{c.prezdivka && <span className="ml-1.5 font-normal text-slate-400">({c.prezdivka})</span>}
                    </div>
                    <PilulkyClenky c={c} />
                  </div>
                  <Stitek ton={c.stav.ton}>{c.stav.text}</Stitek>
                </div>
                <div className="mb-1.5 mt-2 text-[13px] text-slate-500">Naposledy v aplikaci {c.naposledy}</div>
                <Pruh pruh={c.pruh} />
                <div className="mt-2"><CoDela kategorie={c.tyden} /></div>
                <button className={`${tlacitko} mt-2.5 w-full`} onClick={() => otevriDetail(c.email)}>Detail</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 text-left font-semibold sm:px-5">Členka</th>
                  <th className="px-2 py-3 text-left font-semibold">Naposledy v aplikaci</th>
                  <th className="px-2 py-3 text-left font-semibold">Posledních {dni} dní</th>
                  <th className="px-2 py-3 text-left font-semibold">Co dělá (7 dní)</th>
                  <th className="px-4 py-3 sm:px-5" />
                </tr>
              </thead>
              <tbody>
                {videt.map((c) => (
                  <tr key={c.email} className="border-b border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-3 align-middle sm:px-5">
                      <div className="font-semibold text-slate-800">
                        {c.jmeno || c.email}{c.prezdivka && <span className="ml-1.5 font-normal text-slate-400">({c.prezdivka})</span>}
                      </div>
                      <PilulkyClenky c={c} />
                    </td>
                    <td className="px-2 py-3 align-middle">
                      <Stitek ton={c.stav.ton}>{c.stav.text}</Stitek>
                      <div className="mt-1.5 text-slate-700">{c.naposledy}</div>
                    </td>
                    <td className="px-2 py-3 align-middle"><Pruh pruh={c.pruh} /></td>
                    <td className="max-w-[300px] px-2 py-3 align-middle"><CoDela kategorie={c.tyden} /></td>
                    <td className="px-4 py-3 text-right align-middle sm:px-5">
                      <button className={tlacitko} onClick={() => otevriDetail(c.email)}>Detail</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {data && <Legenda />}
      </KartaPrehledu>

      <div ref={detailRef}>
        {detail && data && <Detail c={detail} dnes={data.dnes} dni={dni} onZavrit={() => setVybrana(null)} />}
      </div>
    </Stranka>
  );
}
